import { ColumnDef } from '@tanstack/react-table';
import { ArrowCounterClockwise } from '@phosphor-icons/react';
import { DataTableColumnHeader, StatusChip } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { LeaveRequest } from '../../types/leave.types';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { canWithdrawLeave } from '../../utils/leave-dates';

const LEAVE_STATUSES = ['Pending', 'Approved', 'Rejected', 'Withdrawn', 'Cancelled'];

const LEAVE_TYPE_LABEL: Record<string, string> = {
  FullDay: 'FULL_DAY',
  HalfDay: 'HALF_DAY',
};

interface CreateLeaveHistoryTableColumnsProps {
  t: (key: string) => string;
  onWithdraw: (leaveRequest: LeaveRequest) => void;
  /** Whether the signed-in user holds `hrm::leave::withdraw` (`HRM_PERMISSIONS.LEAVE.WITHDRAW`); the
   * workflow enforces it regardless, this only offers or hides the button. */
  canWithdraw: boolean;
}

export const createLeaveHistoryTableColumns = ({
  t,
  onWithdraw,
  canWithdraw,
}: CreateLeaveHistoryTableColumnsProps): ColumnDef<LeaveRequest>[] => [
  {
    id: 'Title',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LEAVE_CATEGORY')} />,
    accessorFn: (row) => row.Title,
  },
  {
    id: 'LeaveType',
    // Card: under the category, as the old portal's "N Day(s)" line.
    meta: {
      mobile: 'subtitle',
      filter: {
        type: 'options',
        options: Object.entries(LEAVE_TYPE_LABEL).map(([value, label]) => ({
          value,
          label: t(label),
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LEAVE_TYPE')} />,
    accessorFn: (row) => row.LeaveType,
    cell: ({ row }) => t(LEAVE_TYPE_LABEL[row.original.LeaveType] ?? 'FULL_DAY'),
  },
  {
    id: 'StartDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('START_DATE')} />,
    accessorFn: (row) => row.StartDate,
    cell: ({ row }) => formatDateOnly(row.original.StartDate),
  },
  {
    id: 'EndDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('END_DATE')} />,
    accessorFn: (row) => row.EndDate,
    cell: ({ row }) => formatDateOnly(row.original.EndDate),
  },
  {
    id: 'Reason',
    // Card: in the footer, as in the old portal.
    meta: { filter: { type: 'text' }, mobile: 'footer' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('REASON')} />,
    accessorFn: (row) => row.Reason?.trim() || '-',
  },
  {
    id: 'Status',
    meta: {
      filter: {
        type: 'options',
        options: LEAVE_STATUSES.map((status) => ({
          value: status,
          label: t(status.toUpperCase()),
        })),
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Status,
    cell: ({ row }) => (
      <StatusChip status={row.original.Status}>{t(row.original.Status.toUpperCase())}</StatusChip>
    ),
  },
  {
    id: 'actions',
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) =>
      canWithdraw && canWithdrawLeave(row.original) ? (
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('WITHDRAW')}
          title={t('WITHDRAW')}
          onClick={() => onWithdraw(row.original)}
        >
          <ArrowCounterClockwise className="size-5" />
        </Button>
      ) : null,
  },
];
